/* Checkout page with 3-step stepper (Gegevens, Levering, Betaalmethode).
   Updated: WhatsApp link now uses the main number +31 85 02 33 564.
   Updated: Delivery is now a single chosen delivery day (no 3-day period); deliveryWeek holds that day.
   Updated: Checkout now lives at /winkelwagen/afrekenen.
   Updated: Payment method and coupon code moved into step 3 of the stepper.
   Updated: Order summary is sticky on desktop and collapsible in a mobile bottom bar.
   Updated: Stepper indicator uses a centered three-column layout.
   Updated: No default delivery type selected, user must choose explicitly.
   Updated: Order insert no longer sends the order status; the database sets it.
   Updated: AFHALEN20 is restricted to pickup orders containing only Kwekerij products.
   Updated: Added redirecting state so the user sees a "Je wordt doorgestuurd om te betalen"
   message instead of the empty-cart screen during the Mollie redirect window.
   Updated: Added DataFast coupon_applied goal.
   Updated: Pass datafast_visitor_id cookie to Mollie payment for revenue attribution.
   Updated: Improved error handling — order insert failures now show a clear toast
   with the actual error message instead of a generic alert, and the user is kept
   on the checkout page so they can retry. The Mollie fallback path also surfaces
   the error to the user.
   Updated: Order, order items, coupon usage and the confirmation mail job are stored in one database
   transaction (place_order). The browser no longer sends or routes the bank-transfer mail; the backend
   queue sends it via the provider recorded on the order's mail job. */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../contexts/CartContext';
import { supabase } from '../lib/supabase';
import { calculatePriceExclBTW } from '../lib/btw-calculator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactBar from '../components/ContactBar';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import DeliverySelector from '../components/checkout/DeliverySelector';

import UpsellSection from '../components/checkout/UpsellSection';
import { Loader as Loader2, ChevronDown, Tag, Truck, Info, CircleAlert as AlertCircle, CreditCard, Wallet, Lock, ShieldCheck, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { trackGoal } from '@/lib/datafast';

const STEPS = [
  { num: 1, label: 'Gegevens' },
  { num: 2, label: 'Levering' },
  { num: 3, label: 'Betaalmethode' },
];

export default function CartPage() {
  const { items, total, totalDiscount, clearCart, removeItem, exceedsPalletLimit, totalPalletPlaces } = useCart();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [step, setStep] = useState(1);
  const stepperRef = useRef<HTMLDivElement>(null);

  const [paymentMethod, setPaymentMethod] = useState<'mollie' | 'bank_transfer'>('mollie');

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_street: '',
    customer_housenumber: '',
    customer_housenumber_addition: '',
    customer_city: '',
    customer_postal_code: '',
    customer_notes: '',
  });
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [deliveryType, setDeliveryType] = useState<'delivery' | 'pickup' | null>('delivery');
  const [deliveryWeek, setDeliveryWeek] = useState('');
  const [pickupTimeslot, setPickupTimeslot] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const bomenInCart = items.filter(item => item.category_slug === 'bomen');
    if (bomenInCart.length > 0) {
      bomenInCart.forEach(item => removeItem(item.id));
      toast.info('Bomen kunnen alleen via een projectprijs aangevraagd worden. We hebben ze uit je winkelwagen verwijderd.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const [deliverySettings, setDeliverySettings] = useState<{
    tuinplanten_fee_cents: number;
    haagplanten_fee_cents: number;
    free_from_amount_cents: number;
    tuinplanten_minimum_cents: number;
  } | null>(null);
  const [addressComplete, setAddressComplete] = useState(false);

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [isCouponOpen, setIsCouponOpen] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);

  useEffect(() => {
    const isComplete = Boolean(formData.customer_street && formData.customer_housenumber && formData.customer_postal_code && formData.customer_city);
    setAddressComplete(isComplete);
  }, [formData.customer_street, formData.customer_housenumber, formData.customer_postal_code, formData.customer_city]);

  useEffect(() => {
    supabase
      .from('delivery_settings')
      .select('setting_value')
      .eq('setting_key', 'category_delivery_fees')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.setting_value) setDeliverySettings(data.setting_value);
      });
  }, []);

  useEffect(() => {
    if (step > 1) stepperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const tuinplantenItems = items.filter(item => item.category_slug === 'tuinplanten');
  const haagplantenItems = items.filter(item => item.category_slug === 'haagplanten');
  const bomenItems = items.filter(item => item.category_slug === 'bomen');
  const tuinplantenSubtotal = tuinplantenItems.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
  const hasTuinplanten = tuinplantenItems.length > 0;
  const hasHaagplanten = haagplantenItems.length > 0;
  const hasBomen = bomenItems.length > 0;
  const hasTransportOnlyItems = items.some((item) => item.supplier === 'Overig');

  const deliveryFee = deliveryType === 'delivery' && deliverySettings
    ? (total >= deliverySettings.free_from_amount_cents / 100
        ? 0
        : (hasTuinplanten ? deliverySettings.tuinplanten_fee_cents / 100 : 0)
          + (hasHaagplanten ? deliverySettings.haagplanten_fee_cents / 100 : 0)
          + (hasBomen ? (deliverySettings.bomen_fee_cents || deliverySettings.haagplanten_fee_cents) / 100 : 0))
    : 0;
  const transportIsFree = deliveryType === 'delivery' && deliverySettings && total >= deliverySettings.free_from_amount_cents / 100;
  const tuinplantenMinimumMet = !hasTuinplanten || (deliverySettings ? tuinplantenSubtotal >= deliverySettings.tuinplanten_minimum_cents / 100 : true);

  let couponDiscount = 0;
  if (appliedCoupon) {
    couponDiscount = appliedCoupon.discount_type === 'percentage'
      ? (total * appliedCoupon.discount_value) / 100
      : appliedCoupon.discount_value;
  }

  const finalTotal = total + deliveryFee - couponDiscount;

  const subtotalExclBTW = items.reduce((sum, item) => {
    const btwPercentage = item.btw_percentage || 9;
    return sum + calculatePriceExclBTW(item.unit_price, btwPercentage) * item.quantity;
  }, 0);
  const totalBTWAmount = total - subtotalExclBTW;

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(price);

  const isStep1Valid = () =>
    !!formData.customer_email && /\S+@\S+\.\S+/.test(formData.customer_email) &&
    !!formData.customer_name && !!formData.customer_phone &&
    !!formData.customer_street && !!formData.customer_housenumber && !!formData.customer_postal_code && !!formData.customer_city;

  const isStep2Valid = () =>
    deliveryType !== null &&
    (deliveryType === 'delivery' ? !!deliveryWeek : !!pickupTimeslot);

  const validateCoupon = async () => {
    if (!couponCode.trim()) { setCouponError('Voer een kortingscode in'); return; }
    setIsValidatingCoupon(true);
    setCouponError(null);
    try {
      const code = couponCode.trim().toUpperCase();
      if (code === 'AFHALEN20') {
        if (deliveryType !== 'pickup') {
          setCouponError('Code AFHALEN20 is alleen geldig bij afhalen in Dedemsvaart');
          return;
        }
        const allKwekerij = items.every(item => item.supplier === 'Kwekerij');
        if (!allKwekerij) {
          setCouponError('Code AFHALEN20 is alleen geldig voor producten van eigen kwekerij');
          return;
        }
      }
      const { data: coupons, error } = await supabase.rpc('validate_coupon_code', {
        p_code: code, p_order_total: total,
      });
      if (error) throw error;
      const coupon = Array.isArray(coupons) ? coupons[0] : coupons;
      if (!coupon) { setCouponError('Ongeldige kortingscode of niet geldig voor deze bestelling'); return; }
      setAppliedCoupon(coupon);
      setIsCouponOpen(true);
      toast.success('Kortingscode toegepast!');
      trackGoal('coupon_applied', { coupon_code: coupon.code });
    } catch { setCouponError('Fout bij valideren kortingscode'); }
    finally { setIsValidatingCoupon(false); }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setCouponCode(''); setCouponError(null); };

  useEffect(() => { if (appliedCoupon) setIsCouponOpen(true); }, [appliedCoupon]);

  useEffect(() => {
    if (appliedCoupon?.code === 'AFHALEN20' && deliveryType !== 'pickup') {
      setAppliedCoupon(null);
      setCouponCode('');
      setCouponError('Code AFHALEN20 is verwijderd — alleen geldig bij afhalen');
    }
  }, [appliedCoupon, deliveryType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step !== 3) return;
    if (!deliveryType) { alert('Selecteer een levermethode'); return; }
    if (deliveryType === 'delivery' && !deliveryWeek) { alert('Selecteer een bezorgdag'); return; }
    if (deliveryType === 'pickup' && hasTransportOnlyItems) {
      alert('Producten van leverancier Overig kunnen alleen worden bezorgd.');
      setDeliveryType('delivery'); setPickupTimeslot(''); return;
    }
    if (deliveryType === 'pickup' && !pickupTimeslot) { alert('Selecteer een afhaaltijdstip'); return; }
    if (hasTuinplanten && deliverySettings && tuinplantenSubtotal < deliverySettings.tuinplanten_minimum_cents / 100) {
      alert(`Minimum bestelbedrag voor tuinplanten is ${(deliverySettings.tuinplanten_minimum_cents / 100).toFixed(2).replace('.', ',')} euro`); return;
    }

    setIsSubmitting(true);
    try {
      const customer_address = [formData.customer_street, formData.customer_housenumber, formData.customer_housenumber_addition].filter(Boolean).join(' ').trim();
      const orderInsert = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_address,
        customer_city: formData.customer_city,
        customer_postal_code: formData.customer_postal_code,
        customer_notes: formData.customer_notes,
        payment_method: paymentMethod,
      };

      const orderItems = items.map((item) => ({
        product_variant_id: item.product_variant_id,
        product_id: item.product_id || null, product_name: item.product_name,
        variant_size: item.variant_size, quantity: item.quantity,
        unit_price: item.unit_price, total_price: item.unit_price * item.quantity,
        btw_percentage: item.btw_percentage || 9,
      }));

      const { data: placed, error: orderError } = await supabase.rpc('place_order', {
        p_order: {
          ...orderInsert, subtotal: total, tax: 0, delivery_fee: deliveryFee,
          coupon_code: appliedCoupon?.code || null, coupon_discount: couponDiscount,
          total: finalTotal, delivery_type: deliveryType,
          delivery_week: deliveryType === 'delivery' ? deliveryWeek : null,
          pickup_timeslot: deliveryType === 'pickup' ? pickupTimeslot : null,
          calculated_distance_km: null, calculated_pallets: null,
          internal_delivery_cost: null, oegema_delivery_cost: null,
          selected_delivery_method: 'flat_rate', delivery_calculation_data: null,
        },
        p_items: orderItems,
        p_coupon_id: appliedCoupon?.id ?? null,
      });
      if (orderError) throw orderError;
      const order = placed as { id?: string; order_number?: string } | null;
      if (!order?.id || !order.order_number) throw new Error('Bestelling opgeslagen zonder bestelnummer; neem contact met ons op.');

      if (newsletterOptIn) {
        try { await supabase.from('newsletter_subscribers').insert({ email: formData.customer_email, name: formData.customer_name }); }
        catch { /* duplicate */ }
      }

      await clearCart();
      trackGoal('checkout_started', { order_number: order.order_number, total: finalTotal });

      if (paymentMethod === 'mollie') {
        setIsRedirecting(true);
        try {
          const datafastVisitorId = document.cookie
            .split('; ')
            .find((c) => c.startsWith('datafast_visitor_id='))
            ?.split('=')[1] ?? undefined;
          const mollieRes = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-mollie-payment`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_id: order.id, redirect_origin: window.location.origin, datafast_visitor_id: datafastVisitorId }),
          });
          if (!mollieRes.ok) {
            let errDetail = `HTTP ${mollieRes.status}`;
            try { const errBody = await mollieRes.json(); errDetail = errBody.error || errDetail; } catch { /* not JSON */ }
            throw new Error(errDetail);
          }
          const mollieData = await mollieRes.json();
          if (mollieData.checkout_url) { window.location.href = mollieData.checkout_url; return; }
          throw new Error('No checkout URL returned');
        } catch (mollieErr: unknown) {
          setIsRedirecting(false);
          const mollieMessage = mollieErr instanceof Error ? mollieErr.message : 'Onbekende fout';
          toast.error('Betaling kon niet worden gestart', {
            description: `Je bestelling (${order.order_number}) is geplaatst. Je kunt later betalen via de bevestigingspagina. Fout: ${mollieMessage}`,
            duration: 10000,
          });
          navigate(`/order-confirmation?order=${order.order_number}`, { state: { customer: { ...formData, customer_address } } });
          return;
        }
      }

      navigate(`/order-confirmation?order=${order.order_number}`, { state: { customer: formData } });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Er is een fout opgetreden bij het plaatsen van uw bestelling. Probeer het opnieuw.';
      toast.error('Bestelling plaatsen mislukt', {
        description: message,
        duration: 8000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isRedirecting) {
    return (
      <>
        <ContactBar />
        <Header />
        <div className="flex min-h-[60vh] items-center justify-center bg-[#fcfaf6] px-5 py-20">
          <div className="max-w-md text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-xl bg-[#1e2b22] text-white">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
            <h2 className="text-3xl tracking-[-0.035em] text-[#1e2b22]">Je wordt doorgestuurd om te betalen</h2>
            <p className="mt-3 text-sm leading-6 text-[#5a6552]">Eén moment, we brengen je naar de veilige betaalomgeving van Mollie.</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (isSubmitting) {
    return (
      <>
        <ContactBar />
        <Header />
        <div className="flex min-h-[60vh] items-center justify-center bg-[#fcfaf6] px-5 py-20">
          <div className="max-w-md text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-xl bg-[#1e2b22] text-white">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
            <h2 className="text-3xl tracking-[-0.035em] text-[#1e2b22]">Je bestelling wordt verwerkt</h2>
            <p className="mt-3 text-sm leading-6 text-[#5a6552]">Eén moment, we ronden je bestelling af.</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (items.length === 0) {
    return (
      <>
        <ContactBar />
        <Header />
        <div className="flex min-h-[60vh] items-center justify-center bg-[#fcfaf6] px-5 py-20">
          <div className="max-w-md text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-xl border border-[#5a6552] bg-white text-[#5a6552]">
              <svg className="h-8 w-8" strokeWidth={1.4} viewBox="-1.66 0 740.824 740.824" fill="currentColor">
                <path fillRule="evenodd" clipRule="evenodd" d="M630.056 107.658C560.727 38.271 468.525.039 370.294 0 167.891 0 3.16 164.668 3.079 367.072c-.027 64.699 16.883 127.855 49.016 183.523L0 740.824l194.666-51.047c53.634 29.244 114.022 44.656 175.481 44.682h.151c202.382 0 367.128-164.689 367.21-367.094.039-98.088-38.121-190.32-107.452-259.707m-259.758 564.8h-.125c-54.766-.021-108.483-14.729-155.343-42.529l-11.146-6.613-115.516 30.293 30.834-112.592-7.258-11.543c-30.552-48.58-46.689-104.729-46.665-162.379C65.146 198.865 202.065 62 370.419 62c81.521.031 158.154 31.81 215.779 89.482s89.342 134.332 89.311 215.859c-.07 168.242-136.987 305.117-305.211 305.117m167.415-228.514c-9.176-4.591-54.286-26.782-62.697-29.843-8.41-3.061-14.526-4.591-20.644 4.592-6.116 9.182-23.7 29.843-29.054 35.964-5.351 6.122-10.703 6.888-19.879 2.296-9.175-4.591-38.739-14.276-73.786-45.526-27.275-24.32-45.691-54.36-51.043-63.542-5.352-9.183-.569-14.148 4.024-18.72 4.127-4.11 9.175-10.713 13.763-16.07 4.587-5.356 6.116-9.182 9.174-15.303 3.059-6.122 1.53-11.479-.764-16.07-2.294-4.591-20.643-49.739-28.29-68.104-7.447-17.886-15.012-15.466-20.644-15.746-5.346-.266-11.469-.323-17.585-.323-6.117 0-16.057 2.296-24.468 11.478-8.41 9.183-32.112 31.374-32.112 76.521s32.877 88.763 37.465 94.885c4.587 6.122 64.699 98.771 156.741 138.502 21.891 9.45 38.982 15.093 52.307 19.323 21.981 6.979 41.983 5.994 57.793 3.633 17.628-2.633 54.285-22.19 61.932-43.616 7.646-21.426 7.646-39.791 5.352-43.617-2.293-3.826-8.41-6.122-17.585-10.714"/>
              </svg>
            </div>
            <h2 className="text-3xl tracking-[-0.035em] text-[#1e2b22]">Je winkelwagen is leeg</h2>
            <p className="mt-3 text-sm leading-6 text-[#5a6552]">Voeg mooie planten toe om jouw tuin compleet te maken.</p>
            <button
              onClick={() => navigate('/assortiment')}
              className="mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-[#1e2b22] px-7 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(30,43,34,0.18)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#5a6552] hover:shadow-[0_14px_34px_rgba(30,43,34,0.2)]"
            >
              Bekijk assortiment
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const primaryBtnClass = "inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-md bg-[#1e2b22] px-6 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(30,43,34,0.18)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#5a6552] hover:shadow-[0_14px_34px_rgba(30,43,34,0.2)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none";
  const secondaryBtnClass = "inline-flex min-h-12 items-center justify-center gap-2 rounded-md border border-[#e7e1d6] bg-white px-6 text-sm font-semibold text-[#1e2b22] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#fcfaf6]";

  return (
    <>
      <Helmet>
        <title>Jouw winkelwagentje | Buitn Planten</title>
        <meta name="description" content="Bekijk uw winkelwagen en rond uw bestelling af. Topkwaliteit haagplanten uit Dedemsvaart. Veilig en eenvoudig bestellen." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://buitnplanten.nl/winkelwagen/afrekenen" />
      </Helmet>

      <ContactBar />
      <Header />
      <div className="relative isolate bg-[#fcfaf6] pb-20 pt-8 text-[#1e2b22]">
        {/* Radial gradient for depth, same as homepage sections */}
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(100%_60%_at_50%_0%,rgba(137,146,127,0.08),transparent_60%)]"
          aria-hidden="true"
        />

        <div className="relative mx-auto max-w-[1240px] px-5 lg:px-8">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Winkelwagen' }]} />

          {/* Section header, matches homepage pattern */}
          <div className="mb-10 mt-4">
            <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Rond je bestelling af</p>
            <h1 className="text-4xl tracking-[-0.035em] text-[#1e2b22] md:text-5xl">Afrekenen</h1>
            <p className="mt-3 text-base leading-7 text-[#5a6552] sm:leading-8">
              Heb je alles kunnen vinden? Weet dat we je altijd persoonlijk kunnen helpen door een
              <a
                href="https://wa.me/31850233564"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-1 font-medium text-[#1e2b22] transition-colors hover:text-[#5a6552] ml-1 mr-1 underline-offset-2 hover:underline"
              >
                <svg className="h-4 w-4" viewBox="-1.66 0 740.824 740.824" fill="currentColor">
                  <path fillRule="evenodd" clipRule="evenodd" d="M630.056 107.658C560.727 38.271 468.525.039 370.294 0 167.891 0 3.16 164.668 3.079 367.072c-.027 64.699 16.883 127.855 49.016 183.523L0 740.824l194.666-51.047c53.634 29.244 114.022 44.656 175.481 44.682h.151c202.382 0 367.128-164.689 367.21-367.094.039-98.088-38.121-190.32-107.452-259.707m-259.758 564.8h-.125c-54.766-.021-108.483-14.729-155.343-42.529l-11.146-6.613-115.516 30.293 30.834-112.592-7.258-11.543c-30.552-48.58-46.689-104.729-46.665-162.379C65.146 198.865 202.065 62 370.419 62c81.521.031 158.154 31.81 215.779 89.482s89.342 134.332 89.311 215.859c-.07 168.242-136.987 305.117-305.211 305.117m167.415-228.514c-9.176-4.591-54.286-26.782-62.697-29.843-8.41-3.061-14.526-4.591-20.644 4.592-6.116 9.182-23.7 29.843-29.054 35.964-5.351 6.122-10.703 6.888-19.879 2.296-9.175-4.591-38.739-14.276-73.786-45.526-27.275-24.32-45.691-54.36-51.043-63.542-5.352-9.183-.569-14.148 4.024-18.72 4.127-4.11 9.175-10.713 13.763-16.07 4.587-5.356 6.116-9.182 9.174-15.303 3.059-6.122 1.53-11.479-.764-16.07-2.294-4.591-20.643-49.739-28.29-68.104-7.447-17.886-15.012-15.466-20.644-15.746-5.346-.266-11.469-.323-17.585-.323-6.117 0-16.057 2.296-24.468 11.478-8.41 9.183-32.112 31.374-32.112 76.521s32.877 88.763 37.465 94.885c4.587 6.122 64.699 98.771 156.741 138.502 21.891 9.45 38.982 15.093 52.307 19.323 21.981 6.979 41.983 5.994 57.793 3.633 17.628-2.633 54.285-22.19 61.932-43.616 7.646-21.426 7.646-39.791 5.352-43.617-2.293-3.826-8.41-6.122-17.585-10.714"/>
                </svg>
                WhatsApp berichtje
              </a>
              te sturen!
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
            {/* LEFT COLUMN: 3-step stepper */}
            <div className="space-y-6" ref={stepperRef}>
              {/* Stepper Indicator */}
              <div className="rounded-xl bg-white p-4 ring-1 ring-[#e7e1d6] sm:p-5">
                <div className="mx-auto grid w-full max-w-[520px] grid-cols-3 px-2 sm:px-4">
                  {STEPS.map((s, i) => (
                    <div key={s.num} className={`relative flex min-w-0 flex-col items-center ${i < STEPS.length - 1 ? `after:absolute after:left-[calc(50%+20px)] after:right-[calc(-50%+20px)] after:top-4 after:h-0.5 after:rounded-full after:content-[''] sm:after:top-[18px] ${step > s.num ? 'after:bg-[#1e2b22]' : 'after:bg-[#e7e1d6]'}` : ''}`}>
                      <div className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-all sm:h-9 sm:w-9 ${
                        step > s.num ? 'bg-[#1e2b22] text-white' :
                        step === s.num ? 'bg-[#1e2b22] text-white ring-4 ring-[#1e2b22]/15' :
                        'bg-[#fcfaf6] text-[#8b867d] ring-1 ring-[#e7e1d6]'
                      }`}>
                        {step > s.num ? <Check className="h-4 w-4" /> : s.num}
                      </div>
                      <span className={`mt-1.5 whitespace-nowrap text-[10px] font-medium sm:text-xs ${step >= s.num ? 'text-[#1e2b22]' : 'text-[#8b867d]'}`}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Step 1: Gegevens */}
                {step === 1 && (
                  <div className="rounded-xl bg-white p-6 ring-1 ring-[#e7e1d6] transition-[transform,box-shadow] duration-300 hover:shadow-[0_14px_30px_rgba(30,43,34,0.08)] sm:p-8">
                    <h2 className="text-2xl tracking-[-0.035em] text-[#1e2b22]">Jouw gegevens</h2>
                    <div className="mt-6 space-y-5">
                      <div>
                        <Label htmlFor="customer_email" className="text-sm font-medium text-[#1e2b22]">E-mailadres *</Label>
                        <Input
                          id="customer_email" type="email" required
                          value={formData.customer_email}
                          onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                          className="mt-1.5 border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]"
                        />
                      </div>

                      <div className="flex items-start gap-2.5">
                        <Checkbox id="newsletter" checked={newsletterOptIn} onCheckedChange={(checked) => setNewsletterOptIn(checked === true)} />
                        <label htmlFor="newsletter" className="cursor-pointer text-sm leading-5 text-[#5a6552]">
                          Meld je aan voor onze nieuwsbrief en mis geen acties en exclusieve kortingen!
                        </label>
                      </div>

                      <div>
                        <Label htmlFor="customer_name" className="text-sm font-medium text-[#1e2b22]">Volledige naam *</Label>
                        <Input id="customer_name" required value={formData.customer_name}
                          onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                          className="mt-1.5 border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]" />
                      </div>

                      <div>
                        <Label htmlFor="customer_phone" className="text-sm font-medium text-[#1e2b22]">Telefoonnummer *</Label>
                        <Input id="customer_phone" type="tel" required value={formData.customer_phone}
                          onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                          className="mt-1.5 border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]" />
                      </div>

                      <div>
                        <Label htmlFor="customer_street" className="text-sm font-medium text-[#1e2b22]">Straatnaam *</Label>
                        <Input id="customer_street" required value={formData.customer_street}
                          onChange={(e) => setFormData({ ...formData, customer_street: e.target.value })}
                          placeholder="Bijv. Kerkstraat"
                          className="mt-1.5 border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]" />
                      </div>

                      <div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
                        <div>
                          <Label htmlFor="customer_housenumber" className="text-sm font-medium text-[#1e2b22]">Huisnummer *</Label>
                          <Input id="customer_housenumber" required value={formData.customer_housenumber}
                            onChange={(e) => setFormData({ ...formData, customer_housenumber: e.target.value })}
                            placeholder="12"
                            className="mt-1.5 border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]" />
                        </div>
                        <div>
                          <Label htmlFor="customer_housenumber_addition" className="text-sm font-medium text-[#1e2b22]">Toevoeging</Label>
                          <Input id="customer_housenumber_addition" value={formData.customer_housenumber_addition}
                            onChange={(e) => setFormData({ ...formData, customer_housenumber_addition: e.target.value })}
                            placeholder="A"
                            className="mt-1.5 border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]" />
                        </div>
                        <div>
                          <Label htmlFor="customer_postal_code" className="text-sm font-medium text-[#1e2b22]">Postcode *</Label>
                          <Input id="customer_postal_code" required value={formData.customer_postal_code}
                            onChange={(e) => setFormData({ ...formData, customer_postal_code: e.target.value })}
                            placeholder="1234 AB"
                            className="mt-1.5 border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]" />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="customer_city" className="text-sm font-medium text-[#1e2b22]">Plaats *</Label>
                        <Input id="customer_city" required value={formData.customer_city}
                          onChange={(e) => setFormData({ ...formData, customer_city: e.target.value })}
                          placeholder="Plaatsnaam"
                          className="mt-1.5 border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]" />
                      </div>

                      <p className="text-sm leading-6 text-[#5a6552]">
                        Wil je jouw haag laten{' '}
                        <a href="/aanplantservice" target="_blank" rel="noopener noreferrer" className="font-medium text-[#1e2b22] underline-offset-2 transition-colors hover:text-[#5a6552] hover:underline">
                          aanplanten
                        </a>{' '}
                        door ons?{' '}
                        <a href="/projectprijs-aanvragen" className="font-medium text-[#1e2b22] underline-offset-2 transition-colors hover:text-[#5a6552] hover:underline">
                          Vraag dan een projectprijs aan
                        </a>
                      </p>

                      <div>
                        <Label htmlFor="customer_notes" className="text-sm font-medium text-[#1e2b22]">Opmerkingen (optioneel)</Label>
                        <Textarea id="customer_notes" rows={3} value={formData.customer_notes}
                          onChange={(e) => setFormData({ ...formData, customer_notes: e.target.value })}
                          placeholder="Speciale wensen of leverinstructies..."
                          className="mt-1.5 border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]" />
                      </div>

                      {exceedsPalletLimit && (
                        <div className="rounded-xl bg-[#fff8e9] p-4 ring-1 ring-[#d8c69c]">
                          <div className="flex items-start gap-2.5">
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#a47b21]" />
                            <p className="text-sm leading-5 text-[#6e5520]">
                              Je bestelling overschrijdt de maximale capaciteit ({totalPalletPlaces} / 15 palletplaatsen). Neem contact met ons op voor een offerte.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => isStep1Valid() && setStep(2)}
                      disabled={!isStep1Valid()}
                      className={primaryBtnClass + " mt-6 w-full"}
                    >
                      Volgende
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Step 2: Levering */}
                {step === 2 && (
                  <>
                    <DeliverySelector
                      deliveryType={deliveryType} deliveryWeek={deliveryWeek} pickupTimeslot={pickupTimeslot}
                      onDeliveryTypeChange={setDeliveryType as (type: 'delivery' | 'pickup') => void} onDeliveryWeekChange={setDeliveryWeek}
                      onPickupTimeslotChange={setPickupTimeslot}
                      allowPickup={!hasTransportOnlyItems} addressComplete={addressComplete}
                    />

                    <div className="flex gap-3">
                      <button type="button" onClick={() => setStep(1)} className={secondaryBtnClass}>
                        <ArrowLeft className="h-4 w-4" />
                        Terug
                      </button>
                      <button
                        type="button"
                        onClick={() => isStep2Valid() && setStep(3)}
                        disabled={!isStep2Valid()}
                        className={primaryBtnClass}
                      >
                        Volgende
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}

                {/* Step 3: Betaalmethode */}
                {step === 3 && (
                  <>
                    {/* Summary of previous steps */}
                    <div className="rounded-xl bg-white p-5 ring-1 ring-[#e7e1d6] sm:p-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-[#1e2b22]">Controleer je gegevens</h3>
                        <button type="button" onClick={() => setStep(1)} className="text-xs text-[#5a6552] transition-colors hover:text-[#1e2b22] hover:underline">
                          Bewerken
                        </button>
                      </div>
                      <div className="mt-3 space-y-1.5 text-sm text-[#5a6552]">
                        <p><span className="text-[#8b867d]">Naam:</span> {formData.customer_name}</p>
                        <p><span className="text-[#8b867d]">E-mail:</span> {formData.customer_email}</p>
                        <p><span className="text-[#8b867d]">Telefoon:</span> {formData.customer_phone}</p>
                        <p><span className="text-[#8b867d]">Adres:</span> {[formData.customer_street, formData.customer_housenumber, formData.customer_housenumber_addition].filter(Boolean).join(' ')}, {formData.customer_postal_code} {formData.customer_city}</p>
                        <p><span className="text-[#8b867d]">Levering:</span> {deliveryType === 'delivery' ? 'Bezorging' : 'Afhalen in Dedemsvaart'}</p>
                      </div>
                    </div>

                    {/* Payment Method Card */}
                    <div className="rounded-xl bg-white p-6 ring-1 ring-[#e7e1d6] transition-[transform,box-shadow] duration-300 hover:shadow-[0_14px_30px_rgba(30,43,34,0.08)] sm:p-8">
                      <h2 className="text-xl tracking-[-0.035em] text-[#1e2b22]">Betaalmethode</h2>
                      <div className="mt-5 space-y-3">
                        <button type="button" onClick={() => setPaymentMethod('mollie')}
                          className={`w-full rounded-xl p-4 text-left ring-1 transition-all ${
                            paymentMethod === 'mollie' ? 'bg-[#1e2b22]/5 ring-[#1e2b22]' : 'bg-[#fcfaf6] ring-[#e7e1d6] hover:ring-[#5a6552]'
                          }`}>
                          <div className="flex items-start gap-3">
                            <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-2 ${paymentMethod === 'mollie' ? 'bg-[#1e2b22] ring-[#1e2b22]' : 'ring-[#cfcac2]'}`}>
                              {paymentMethod === 'mollie' && <div className="h-2 w-2 rounded-full bg-white" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-[#1e2b22]" /><p className="font-semibold text-[#1e2b22]">Online betalen (Mollie)</p></div>
                              <p className="mt-1 text-sm leading-5 text-[#5a6552]">Betaal direct online via iDEAL, creditcard of andere methoden. Je wordt doorgestuurd naar de veilige betaalomgeving van Mollie.</p>
                            </div>
                          </div>
                        </button>

                        <button type="button" onClick={() => setPaymentMethod('bank_transfer')}
                          className={`w-full rounded-xl p-4 text-left ring-1 transition-all ${
                            paymentMethod === 'bank_transfer' ? 'bg-[#1e2b22]/5 ring-[#1e2b22]' : 'bg-[#fcfaf6] ring-[#e7e1d6] hover:ring-[#5a6552]'
                          }`}>
                          <div className="flex items-start gap-3">
                            <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-2 ${paymentMethod === 'bank_transfer' ? 'bg-[#1e2b22] ring-[#1e2b22]' : 'ring-[#cfcac2]'}`}>
                              {paymentMethod === 'bank_transfer' && <div className="h-2 w-2 rounded-full bg-white" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-[#1e2b22]" /><p className="font-semibold text-[#1e2b22]">Bankoverschrijving</p></div>
                              <p className="mt-1 text-sm leading-5 text-[#5a6552]">Je ontvangt per e-mail de factuur met onze bankgegevens om het bedrag over te maken. De bestelling wordt meteen verwerkt na ontvangst van de betaling.</p>
                            </div>
                          </div>
                        </button>
                      </div>

                      <div className="mt-5 border-t border-[#e7e1d6] pt-4">
                        <p className="text-center text-[11px] font-medium uppercase tracking-[0.16em] text-[#8b867d]">Betaal eenvoudig met</p>
                        <div className="mt-3 flex items-center justify-center gap-3">
                          <img src="/assets/payments/ideal-wero.svg" alt="iDEAL en Wero" className="h-6 w-auto" />
                          <img src="/assets/payments/apple-pay.svg" alt="Apple Pay" className="h-6 w-auto" />
                          <img src="/assets/payments/bancontact.svg" alt="Bancontact" className="h-6 w-auto" />
                          <img src="/assets/payments/sepa.svg" alt="SEPA" className="h-6 w-auto" />
                        </div>
                      </div>
                    </div>

                    {/* Coupon Code Section */}
                    <div className="rounded-xl border border-[#1e2b22]/15 bg-[#f2ede4] p-5 sm:p-6">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#5a6552]">Afhalen in Dedemsvaart</p>
                      <p className="mt-1 text-lg font-semibold text-[#1e2b22]">20% korting met code AFHALEN20</p>
                      <p className="mt-2 text-sm leading-5 text-[#5a6552]">Kies Ophalen en gebruik deze code wanneer alle producten van leverancier Kwekerij zijn.</p>
                    </div>
                    <div className="rounded-xl bg-white p-6 ring-1 ring-[#e7e1d6] sm:p-8">
                      <Collapsible open={isCouponOpen} onOpenChange={setIsCouponOpen}>
                        <CollapsibleTrigger className="flex w-full items-center justify-between transition-opacity hover:opacity-70">
                          <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-[#5a6552]" /><h3 className="text-base font-semibold text-[#1e2b22]">Heb je een kortingscode?</h3></div>
                          <ChevronDown className={`h-5 w-5 text-[#5a6552] transition-transform ${isCouponOpen ? 'rotate-180' : ''}`} />
                        </CollapsibleTrigger>

                        <CollapsibleContent className="pt-4">
                          {appliedCoupon ? (
                            <div className="rounded-xl bg-[#fcfaf6] p-4 ring-1 ring-[#e7e1d6]">
                              <div className="flex items-center justify-between gap-3">
                                <p className="font-semibold text-[#1e2b22]">Code: {appliedCoupon.code}</p>
                                <Button type="button" variant="ghost" size="sm" onClick={removeCoupon} className="text-[#a47b21] hover:text-[#8a6d3b]">Verwijderen</Button>
                              </div>
                              <p className="mt-1 text-sm text-[#5a6552]">Korting: {appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}%` : formatPrice(appliedCoupon.discount_value)}</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <Input placeholder="Voer kortingscode in" value={couponCode}
                                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); validateCoupon(); } }}
                                  disabled={isValidatingCoupon}
                                  className="border-[#e7e1d6] bg-[#fcfaf6] focus-visible:ring-[#5a6552]" />
                                <Button type="button" onClick={validateCoupon} disabled={isValidatingCoupon || !couponCode.trim()}
                                  className="rounded-md border border-[#5a6552] bg-white text-sm font-semibold text-[#1e2b22] hover:bg-[#fcfaf6]">
                                  {isValidatingCoupon ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Valideren...</> : 'Toepassen'}
                                </Button>
                              </div>
                              {couponError && <p className="text-sm text-[#a47b21]">{couponError}</p>}
                            </div>
                          )}
                        </CollapsibleContent>
                      </Collapsible>
                    </div>

                    {/* Navigation */}
                    <div className="flex gap-3">
                      <button type="button" onClick={() => setStep(2)} className={secondaryBtnClass}>
                        <ArrowLeft className="h-4 w-4" />
                        Terug
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting || exceedsPalletLimit || !deliveryType || (deliveryType === 'delivery' && hasTuinplanten && !tuinplantenMinimumMet)}
                        className={primaryBtnClass}
                      >
                        {isSubmitting ? (
                          <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Bestelling plaatsen...</>
                        ) : exceedsPalletLimit ? (
                          'Neem contact op'
                        ) : (
                          <>Bestelling plaatsen<ArrowRight className="h-4 w-4" /></>
                        )}
                      </button>
                    </div>

                    <div className="flex items-center justify-center gap-4 text-xs text-[#8b867d]">
                      <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />Veilig afrekenen</span>
                      <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Gegevens beschermd</span>
                    </div>

                    <p className="text-center text-xs text-[#8b867d]">
                      {paymentMethod === 'mollie'
                        ? 'Je wordt na het plaatsen doorgeleid naar de veilige betaalomgeving van Mollie.'
                        : 'Na het plaatsen van de bestelling ontvang je een bevestiging per e-mail met betaalinstructies.'}
                    </p>
                  </>
                )}
              </form>
            </div>

            {/* RIGHT COLUMN: order summary */}
            <div className="fixed inset-x-0 bottom-0 z-40 space-y-0 lg:sticky lg:inset-auto lg:top-[176px] lg:z-30 lg:self-start" >
              {/* Order Summary Card */}
              <div className="rounded-t-xl bg-white p-4 shadow-[0_-10px_30px_rgba(30,43,34,0.12)] ring-1 ring-[#e7e1d6] lg:rounded-xl lg:p-6 lg:shadow-none sm:p-8">
                <button type="button" onClick={() => setIsMobileSummaryOpen((open) => !open)} className="flex w-full items-center justify-between text-left lg:pointer-events-none">
                  <h2 className="text-xl tracking-[-0.035em] text-[#1e2b22] sm:text-2xl">Besteloverzicht</h2>
                  <ChevronDown className={`h-5 w-5 text-[#5a6552] transition-transform lg:hidden ${isMobileSummaryOpen ? 'rotate-180' : ''}`} />
                </button>

                <div className={isMobileSummaryOpen ? 'block' : 'hidden lg:block'}>
                {/* Cart items */}
                <div className="mt-6 space-y-3">
                  {items.map((item) => (
                    <div key={item.id} className="flex gap-3 rounded-xl bg-[#fcfaf6] p-3 ring-1 ring-[#e7e1d6]/60">
                      {item.image_url && (
                        <div className="hidden h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-white sm:block">
                          <img src={item.image_url} alt={item.product_name} className="h-full w-full object-cover" />
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-sm font-semibold leading-5 text-[#1e2b22]">
                            {item.product_name}{item.variant_size ? ` ${item.variant_size}` : ''}
                          </h3>
                          <p className="shrink-0 text-sm font-bold text-[#1e2b22]">{formatPrice(item.unit_price)}</p>
                        </div>

                        {item.variant_type && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <div
                              className={`inline-flex items-center gap-1.5 rounded-[5px] border px-2 py-1 text-[11px] font-medium ${
                                item.variant_type === 'Kale wortel' ? 'border-[#8a6d3b] text-[#8a6d3b]' : 'border-[#5a6552] text-[#5a6552]'
                              } bg-white`}
                            >
                              <img
                                src={`/assets/${item.variant_type === 'Kale wortel' ? 'kale-wortel.webp' : item.variant_type === 'Kluit' ? 'met-kluit.webp' : 'in-pot.webp'}`}
                                alt={item.variant_type} className="h-4 w-4 object-contain"
                              />
                              {item.variant_type}
                            </div>
                            {item.supplier === 'Overig' && (
                              <button
                                onClick={() => setShowDeliveryModal(true)}
                                className="inline-flex items-center gap-1.5 rounded-[5px] border border-[#1e2b22] bg-[#1e2b22]/5 px-2 py-1 text-[11px] font-medium text-[#1e2b22] transition-shadow hover:shadow-md"
                              >
                                <Truck className="h-3.5 w-3.5" />Alleen bezorgen<Info className="h-3 w-3 text-[#8b867d]" />
                              </button>
                            )}
                          </div>
                        )}

                        <div className="mt-2.5 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-[#8b867d]">Aantal:</span>
                            <span className="text-sm font-bold text-[#1e2b22]">{item.quantity}</span>
                          </div>
                          <p className="text-base font-bold text-[#1e2b22]">{formatPrice(item.unit_price * item.quantity)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Free shipping progress */}
                {deliveryType === 'delivery' && deliverySettings && !transportIsFree && (
                  <div className="mt-5 rounded-xl bg-white p-4 ring-1 ring-[#e7e1d6]">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#fcfaf6] text-[#5a6552]">
                        <Truck className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#1e2b22]">
                          Nog {formatPrice(Math.max(0, deliverySettings.free_from_amount_cents / 100 - total))} voor gratis bezorging
                        </p>
                        <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-[#e7e1d6]">
                          <div className="h-full rounded-full bg-[#5a6552] transition-all duration-500"
                            style={{ width: `${Math.min(100, (total / (deliverySettings.free_from_amount_cents / 100)) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tuinplanten minimum warning */}
                {deliveryType === 'delivery' && hasTuinplanten && !tuinplantenMinimumMet && deliverySettings && (
                  <div className="mt-4 rounded-xl bg-[#fff8e9] p-4 ring-1 ring-[#d8c69c]">
                    <div className="flex items-start gap-2.5">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#a47b21]" />
                      <p className="text-sm leading-5 text-[#6e5520]">
                        Minimum bestelbedrag voor tuinplanten is {(deliverySettings.tuinplanten_minimum_cents / 100).toFixed(2).replace('.', ',')} euro. Nog {formatPrice(Math.max(0, deliverySettings.tuinplanten_minimum_cents / 100 - tuinplantenSubtotal))} toevoegen aan tuinplanten.
                      </p>
                    </div>
                  </div>
                )}

                {/* Price Breakdown */}
                <div className="mt-6 space-y-2.5 border-t border-[#e7e1d6] pt-5">
                  <div className="flex justify-between text-sm"><span className="text-[#5a6552]">Subtotaal (excl. BTW)</span><span className="font-medium text-[#1e2b22]">{formatPrice(subtotalExclBTW)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#5a6552]">BTW</span><span className="font-medium text-[#1e2b22]">{formatPrice(totalBTWAmount)}</span></div>

                  {deliveryType === 'delivery' && !deliverySettings && (
                    <div className="flex items-center gap-2 py-1 text-sm text-[#5a6552]"><Loader2 className="h-4 w-4 animate-spin" /><span>Bezorgkosten laden...</span></div>
                  )}

                  {deliveryType === 'delivery' && deliverySettings && (
                    <div>
                      <div className="flex justify-between text-sm"><span className="text-[#5a6552]">Bezorgkosten (incl. BTW)</span><span className="font-medium text-[#1e2b22]">{transportIsFree ? 'Gratis' : formatPrice(deliveryFee)}</span></div>
                      {!transportIsFree && hasTuinplanten && (hasHaagplanten || hasBomen) && (
                        <p className="mt-1 text-xs text-[#8b867d]">
                          Tuinplanten {formatPrice(deliverySettings.tuinplanten_fee_cents / 100)}{hasHaagplanten ? ` + Haagplanten ${formatPrice(deliverySettings.haagplanten_fee_cents / 100)}` : ''}{hasBomen ? ` + Bomen ${formatPrice((deliverySettings.bomen_fee_cents || deliverySettings.haagplanten_fee_cents) / 100)}` : ''}
                        </p>
                      )}
                      {!transportIsFree && <p className="mt-1 text-xs text-[#8b867d]">Gratis bezorging vanaf {formatPrice(deliverySettings.free_from_amount_cents / 100)}</p>}
                    </div>
                  )}

                  {deliveryType === 'pickup' && (
                    <div className="flex justify-between text-sm"><span className="text-[#5a6552]">Bezorging</span><span className="font-medium text-[#1e2b22]">€ 0,-</span></div>
                  )}

                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-[#5a6552]">Kortingscode ({appliedCoupon.code})</span><span className="font-semibold text-[#5a6552]">- {formatPrice(couponDiscount)}</span></div>
                  )}

                  <div className="flex items-end justify-between border-t border-[#e7e1d6] pt-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-[#8b867d]">Totaal incl. BTW</p>
                    <p className="mt-1 whitespace-nowrap text-right text-2xl font-semibold text-[#1e2b22]">{formatPrice(finalTotal)}</p>
                  </div>
                </div>

                </div>
              </div>
            </div>
          </div>

          <div className="mt-16">
            <UpsellSection
              categorySlug="aanplant-en-verzorging"
              title="Weet je zeker dat je alles hebt?"
              subtitle="Voor optimale groei van je planten raden we je aan om ook haagplantenmest erbij te bestellen, voor het aanplanten kun je het best aanplantgrond erbij bestellen."
            />
          </div>
        </div>
      </div>
      <Footer />

      {/* Delivery-only modal */}
      <Dialog open={showDeliveryModal} onOpenChange={setShowDeliveryModal}>
        <DialogContent className="overflow-hidden border-0 p-0 sm:max-w-md" style={{ borderRadius: '12px' }}>
          <div className="flex items-center gap-3 bg-[#1e2b22] px-6 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <Truck className="h-5 w-5 text-white" />
            </div>
            <DialogHeader className="flex-1 space-y-0">
              <DialogTitle className="text-lg font-bold text-white">Dit product is alleen leverbaar via transport</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 py-5">
            <DialogDescription className="text-base leading-relaxed text-[#5a6552]">
              Wij streven naar een totaal aanbod van hoogwaardige planten. Omdat we niet alles zelf kunnen kweken, werken we samen met collega-kwekers die dezelfde kwaliteit nastreven. Deze planten worden rechtstreeks via transport geleverd en zijn daarom niet beschikbaar voor afhaling.
            </DialogDescription>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setShowDeliveryModal(false)}
                className="inline-flex min-h-10 items-center justify-center rounded-md bg-[#1e2b22] px-6 py-2 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#5a6552]"
              >
                Begrepen
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
